import { Injectable } from '@angular/core';
import { Issue, Resolution } from './issue.dto';
import { map, Observable } from 'rxjs';
import { ChangeTyped, SearchResult, WW_ISSUES_COLLECTION } from '@damofire/data-commons';
import { ObservableIssueService } from './issue-observable.service';
import { FirebaseObservableServiceFactory } from '@damofire/data-angular-commons';
import { TypedDesQuery } from '../des.dto';
import { FirebaseWatsonObservableService } from '../firebase-watson-observable.service';
import { FirebaseObservableNoteService } from '../note/firebase-note-observable.service';

@Injectable()
export class FirebaseObservableIssueService extends ObservableIssueService {
  private readonly firebaseWatsonObservableService: FirebaseWatsonObservableService<Issue>;

  constructor(
    firebaseObservableServiceFactory: FirebaseObservableServiceFactory,
    protected readonly firebaseObservableNoteService: FirebaseObservableNoteService,
  ) {
    super(firebaseObservableNoteService);
    this.firebaseWatsonObservableService = new FirebaseWatsonObservableService<Issue>(firebaseObservableServiceFactory, WW_ISSUES_COLLECTION);
  }

  protected observeById(division: string, id: string): Observable<ChangeTyped<Issue>> {
    return this.firebaseWatsonObservableService.observeDoc(id);
  }

  observeByAssets(divisionId: string, ids: number[]): Observable<ChangeTyped<Issue>[]> {
    return this.firebaseWatsonObservableService.observeByWarehousePiped(
      divisionId,
      map((fromFirestore) => {
        return fromFirestore.filter((doc) => ids.includes(doc.asset._id as number));
      }),
    );
  }

  protected observeOpen(divisionId: string, section?: string): Observable<ChangeTyped<Issue>[]> {
    if (section) {
      return this.firebaseWatsonObservableService.observeByWarehouseWithSectionPiped(
        divisionId,
        section,
        map((fromFirestore) => {
          return fromFirestore.filter((doc) => doc.resolution === Resolution.OPEN);
        }),
      );
    }
    return this.firebaseWatsonObservableService.observeByWarehousePiped(
      divisionId,
      map((fromFirestore) => {
        return fromFirestore.filter((doc) => doc.resolution === Resolution.OPEN);
      }),
    );
  }

  protected observeByInspection(divisionId: string, id: string): Observable<ChangeTyped<Issue>[]> {
    return this.firebaseWatsonObservableService.observeByWarehousePiped(
      divisionId,
      map((fromFirestore) => {
        return fromFirestore.filter((doc) => doc.inspection?._id === +id);
      }),
    );
  }

  observeByAsset(divisionId: string, assetId: number, inspectionId?: string): Observable<ChangeTyped<Issue>[]> {
    return this.firebaseWatsonObservableService.observeByWarehousePiped(
      divisionId,
      map((fromFirestore) => {
        return fromFirestore.filter((doc) => {
          let found = doc.asset._id === +assetId;

          if (found && inspectionId) {
            found = doc.inspection?._id === +inspectionId;
          }

          return found;
        });
      }),
    );
  }

  observeOpenByAsset(divisionId: string, assetId: number): Observable<ChangeTyped<Issue>[]> {
    return this.firebaseWatsonObservableService.observeByWarehousePiped(
      divisionId,
      map((fromFirestore) => {
        return fromFirestore.filter((doc) => doc.asset._id === assetId && doc.resolution === Resolution.OPEN);
      }),
    );
  }

  protected noteableSearch(id: string, sQuery: TypedDesQuery): Observable<SearchResult<Issue>> {
    return this.firebaseWatsonObservableService.search(id, sQuery, this.SEARCH_FIELDS);
  }
}
